import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { EMPTY, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { FileHelper } from '../../../../../../core/helpers/file/file-helper';
import { ReportService } from '../../../../shared/services/report.service';
import {
    QuickbooksReport,
    QuickbooksReportDisplayName,
} from './models/quickbooks-report';
import { QuickbooksReportConfig } from './models/quickbooks-report-config';
import { QuickbooksReportConstants } from './models/quickbooks-report-constants';

@Component({
    selector: 'app-quickbooks-report',
    templateUrl: './quickbooks-report.component.html',
    imports: [],
})
export class QuickbooksReportComponent implements OnInit {
    private readonly reportService = inject(ReportService);
    private readonly cdr = inject(ChangeDetectorRef);

    /**
     * Defines the order in which QuickBooks reports should be displayed in the UI.
     */
    quickbooksReportOrder: QuickbooksReport[] = [
        QuickbooksReport.NewOrders,
        QuickbooksReport.NewPaymentsForNewOrders,
        QuickbooksReport.UpdatesOnExportedOrders,
    ];

    quickbooksReportsConfig: Record<QuickbooksReport, QuickbooksReportConfig> =
        {
            [QuickbooksReport.NewOrders]: {
                name: QuickbooksReportDisplayName[QuickbooksReport.NewOrders],
                count: 0,
                getInclusionCount$: () =>
                    this.reportService.getNewOrdersReportInclusion().pipe(
                        map((response) => response.eligibleOrdersCount),
                        catchError(() => of(0)),
                    ),
                getExport$: () => this.reportService.exportNewOrdersReport(),
            },
            [QuickbooksReport.NewPaymentsForNewOrders]: {
                name: QuickbooksReportDisplayName[
                    QuickbooksReport.NewPaymentsForNewOrders
                ],
                count: 0,
                getInclusionCount$: () =>
                    this.reportService.getNewPaymentsReportInclusion().pipe(
                        map((response) => response.eligiblePaymentsCount),
                        catchError(() => of(0)),
                    ),
                getExport$: () => this.reportService.exportNewPaymentsReport(),
            },
            [QuickbooksReport.UpdatesOnExportedOrders]: {
                name: QuickbooksReportDisplayName[
                    QuickbooksReport.UpdatesOnExportedOrders
                ],
                count: 0,
                getInclusionCount$: () =>
                    this.reportService
                        .getUpdatedExportedOrdersReportInclusion()
                        .pipe(
                            map((response) => response.eligibleOrdersCount),
                            catchError(() => of(0)),
                        ),
                getExport$: () =>
                    this.reportService.exportUpdatedExportedOrdersReport(),
            },
        };

    ngOnInit(): void {
        this.loadQuickbooksReportsInclusion();
    }

    export(quickbooksReportKey: QuickbooksReport): void {
        const report = this.quickbooksReportsConfig[quickbooksReportKey];

        if (!report) {
            throw new Error(
                `Report with key: '${quickbooksReportKey}' was not found.`,
            );
        }

        report
            .getExport$()
            .pipe(
                switchMap((response) => {
                    const blob = response.body;
                    if (!blob) {
                        return EMPTY;
                    }

                    const filename =
                        FileHelper.getFilenameFromContentDisposition(
                            response,
                        ) ?? QuickbooksReportConstants.DEFAULT_FILENAME;
                    FileHelper.downloadBlobFile(blob, filename);

                    return report.getInclusionCount$();
                }),
            )
            .subscribe((newCount) => {
                this.quickbooksReportsConfig[quickbooksReportKey].count =
                    newCount;

                this.cdr.detectChanges();
            });
    }

    /**
     * Loads the inclusion counts for all QuickBooks reports and updates their count properties.
     */
    private loadQuickbooksReportsInclusion(): void {
        forkJoin(
            this.quickbooksReportOrder.map((report) =>
                this.quickbooksReportsConfig[report]
                    .getInclusionCount$()
                    .pipe(map((count) => ({ report, count }))),
            ),
        ).subscribe((results) => {
            results.forEach(({ report, count }) => {
                this.quickbooksReportsConfig[report].count = count;
            });

            this.cdr.detectChanges();
        });
    }
}
